import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { Tabs } from 'components/FbUI/Tabs'
import { Select } from 'components/UI/Select/Select'
import { useMemo, useState } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'

type FinishTab = 'matched' | 'unmatched'

export type CustomCompanyType = {
  category_id: number
  subcategory_id: number
} | null

export function ImportWizardFinishMatchingStep({
  show,
  data,
  selectedAction,
  onActionChange,
  selectedCustomCompanyType,
  onCustomCompanyTypeChange,
  skipMatching = false,
}: {
  show: boolean
  data: CSVRow[]
  selectedAction: 'create' | 'exclude'
  onActionChange: (action: 'create' | 'exclude') => void
  selectedCustomCompanyType: CustomCompanyType
  onCustomCompanyTypeChange: (type: CustomCompanyType) => void
  skipMatching?: boolean
}) {
  const viewModel = useImporterViewModel()
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const [tab, setTab] = useState<FinishTab>('matched')

  // Build dropdown options for company types
  const companyTypeOptions = useMemo(() => {
    return (
      viewModel.companySubcategories?.map((subcat) => ({
        label: `${subcat.category_name} / ${subcat.subcategory_name}`,
        value: `${subcat.category_id}-${subcat.value}`,
      })) ?? []
    )
  }, [viewModel.companySubcategories])

  // Get selected value for the dropdown
  const selectedTypeValue = selectedCustomCompanyType
    ? `${selectedCustomCompanyType.category_id}-${selectedCustomCompanyType.subcategory_id}`
    : null

  // Identify company_match columns from headers
  const companyMatchHeaders = useMemo(() => {
    return (
      viewModel.importData?.headers.filter(
        (h: { original: string; mapped: string }) =>
          getColumnOption(h.mapped)?.type === 'company_match'
      ) ?? []
    )
  }, [viewModel.importData?.headers])

  // Split data into matched and unmatched
  const { matchedData, unmatchedData } = useMemo(() => {
    const matched: CSVRow[] = []
    const unmatched: CSVRow[] = []
    data.forEach((row) => {
      const chain = row[InternalColumns.MATCHED_CHAIN]
      const door = row[InternalColumns.MATCHED_DOOR]
      if (chain || door) {
        matched.push(row)
      } else {
        unmatched.push(row)
      }
    })
    return { matchedData: matched, unmatchedData: unmatched }
  }, [data])

  const currentData = tab === 'matched' ? matchedData : unmatchedData

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    const columns: ColumnDef<CSVRow>[] = []

    // Input Data column
    columns.push({
      id: 'input_data',
      header: 'Input Data',
      accessorFn: (row) => {
        return companyMatchHeaders
          .map((h: { original: string }) => row[h.original] || '')
          .join(' ')
          .toLowerCase()
      },
      cell: ({ row }) => {
        const items: { label: string; value: string }[] = []

        companyMatchHeaders.forEach(
          (header: { original: string; mapped: string }) => {
            const value = row.original[header.original]
            if (value) {
              items.push({
                label: getColumnOption(header.mapped)?.name || header.mapped,
                value,
              })
            }
          }
        )

        return (
          <div className="space-y-0.5 text-sm">
            {items.map((item, idx) => (
              <div key={idx} className="text-gray-700">
                <span className="font-medium text-gray-500">{item.label}:</span>{' '}
                {item.value}
              </div>
            ))}
          </div>
        )
      },
    })

    // Matched Data column (sortable)
    columns.push({
      id: 'matched_data',
      header: 'Matched Data',
      accessorFn: (row) => {
        const chain = row[InternalColumns.MATCHED_CHAIN]
        const door = row[InternalColumns.MATCHED_DOOR]
        return (door?.name || chain?.name || '').toLowerCase()
      },
      cell: ({ row }) => {
        const chain = row.original[InternalColumns.MATCHED_CHAIN]
        const door = row.original[InternalColumns.MATCHED_DOOR]
        const isMatched = chain || door

        if (!isMatched) {
          return (
            <div className="text-gray-400 italic flex items-center gap-2">
              <span className="text-gray-400">○</span>
              <span>Unmatched</span>
            </div>
          )
        }

        const items: { label: string; value: string; link?: string | null }[] =
          []

        // Build company ID for link
        const companyId = door
          ? `${door.type}${door.item_id}`
          : chain
            ? `${chain.type}${chain.item_id}`
            : null
        const companyUrl = companyId ? getCompanyUrlFromId(companyId) : null

        if (door?.name || chain?.name) {
          items.push({
            label: 'Name',
            value: door?.name || chain?.name || '-',
            link: companyUrl,
          })
        }
        if (door?.domain || chain?.domain) {
          items.push({
            label: 'Domain',
            value: door?.domain || chain?.domain || '-',
          })
        }
        if (door?.address) {
          items.push({ label: 'Address', value: door.address })
        }
        if (chain?.count || door?.chain_size) {
          items.push({
            label: 'Doors',
            value: String(chain?.count || door?.chain_size || '-'),
          })
        }

        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-600 font-bold">✓</span>
              <span className="font-semibold text-gray-900 text-sm">
                Matched
              </span>
            </div>
            <div className="space-y-0.5 text-sm">
              {items.map((item, idx) => (
                <div key={idx} className="text-gray-700">
                  <span className="font-medium text-gray-500">
                    {item.label}:
                  </span>{' '}
                  {item.link ? (
                    <FbLink to={item.link} target="_blank">
                      {item.value}
                    </FbLink>
                  ) : (
                    item.value
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      },
    })

    return columns
  }, [companyMatchHeaders, getCompanyUrlFromId])

  if (!show) {
    return null
  }

  return (
    <>
      {skipMatching ? (
        // Skip matching mode: Show all companies as custom/unmatched
        <>
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-800">
              <strong>Skip Matching Enabled:</strong> All {data.length}{' '}
              companies will be created as custom (user-generated) companies.
            </p>
          </div>

          <ClientDataTable
            height={520}
            data={data}
            columns={columnDefs}
            enableSorting
          />

          <div className="flex flex-col gap-3 mt-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Company type for all companies:
              </span>
              <Select
                className="w-64"
                items={companyTypeOptions}
                selected={selectedTypeValue}
                onSelect={(value) => {
                  if (!value) return // Prevent clearing selection
                  const [categoryId, subcategoryId] = value
                    .split('-')
                    .map(Number)
                  onCustomCompanyTypeChange({
                    category_id: categoryId,
                    subcategory_id: subcategoryId,
                  })
                }}
              />
            </div>
          </div>
        </>
      ) : (
        // Normal mode: Show matched/unmatched tabs
        <>
          <Tabs
            tabs={[
              {
                key: 'matched',
                title: `Matched (${matchedData.length})`,
              },
              {
                key: 'unmatched',
                title: `Unmatched (${unmatchedData.length})`,
              },
            ]}
            selectedTab={tab}
            setSelectedTab={setTab}
          />

          <ClientDataTable
            height={unmatchedData.length > 0 ? 520 : '100%'}
            data={currentData}
            columns={columnDefs}
            enableSorting
          />

          {unmatchedData.length > 0 && (
            <div className="flex flex-col gap-3 mt-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  Unmatched rows:
                </span>
                <div className="flex gap-1">
                  <FbButton
                    size="sm"
                    variant={
                      selectedAction === 'create' ? 'primary' : 'secondary'
                    }
                    onClick={() => onActionChange('create')}
                  >
                    Create Custom Companies
                  </FbButton>
                  <FbButton
                    size="sm"
                    variant={
                      selectedAction === 'exclude' ? 'primary' : 'secondary'
                    }
                    onClick={() => onActionChange('exclude')}
                  >
                    Exclude from Import
                  </FbButton>
                </div>
              </div>

              {selectedAction === 'create' && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">
                    Company type:
                  </span>
                  <Select
                    className="w-64"
                    items={companyTypeOptions}
                    selected={selectedTypeValue}
                    onSelect={(value) => {
                      if (!value) return // Prevent clearing selection
                      const [categoryId, subcategoryId] = value
                        .split('-')
                        .map(Number)
                      onCustomCompanyTypeChange({
                        category_id: categoryId,
                        subcategory_id: subcategoryId,
                      })
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}
